import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { objectOmit } from '@/_shared/helpers/objectOmit';
import { GenericApiError } from '@/_shared/types/apiError';
import { getCurrentOrganization } from '@/core/state';
import { API_PAYMENTS_GET, KEY_GET_PAYMENTS } from '../constants';
import { MOCK_RESPONSE_GET_PAYMENTS } from '../mocks';
import { GetPaymentsParams, Payment, PaymentFromBE } from '../types';

type FullRequestType = ReturnType<typeof createGeneralCall>['selectors']['request'] & {
  payload: Omit<CallParamsGetPayments, 'successSaga' | 'errorSaga'>;
};

export type CallParamsGetPayments = GetPaymentsParams & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

type CallResponseGetPayments = {
  data: PaymentFromBE[];
  totalItems: number;
};

type CallErrorGetPayments = GenericApiError;

type CallSelectorsGetPayments = {
  data: Payment[];
  totalItems: number;
  fullRequest: FullRequestType;
  apiWithActiveFilters: boolean;
};

let internalPayload = {};

export const getPaymentsCall = createGeneralCall<
  CallParamsGetPayments,
  CallResponseGetPayments,
  CallErrorGetPayments,
  CallSelectorsGetPayments
>({
  id: KEY_GET_PAYMENTS,
  url:
    ({ payload }) =>
    state => {
      const props = objectOmit(payload, ['successSaga', 'errorSaga']);
      const org = getCurrentOrganization(state);
      return API_PAYMENTS_GET(props, org as unknown as string);
    },
  method: 'get',
  paramsSelector:
    ({ payload }) =>
    () => {
      internalPayload = { ...payload };
      return payload;
    },
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_PAYMENTS : undefined,
  selectors: {
    data: ({ selectors }) =>
      createSelector(selectors.response, res => (res?.data as Payment[]) || []),
    totalItems: ({ selectors }) => createSelector(selectors.response, res => res?.totalItems),
    fullRequest: ({ selectors }) =>
      createSelector(selectors.request, request => {
        return {
          ...request,
          payload: { ...internalPayload },
        } as unknown as FullRequestType;
      }),
    apiWithActiveFilters: ({ selectors }) =>
      createSelector(selectors.fullRequest, fullRequest => {
        const { ...filters } = objectOmit(fullRequest.payload, ['start', 'length']);
        return Object.keys(filters).some(
          key =>
            filters[key as keyof typeof filters] !== undefined &&
            filters[key as keyof typeof filters] !== null,
        );
      }),
  },
  responseMapper: ({ response }) => {
    return {
      data:
        response.data?.map(payment => ({ ...payment, currency: payment.currency || 'EUR' })) || [],
      totalItems: response.totalItems || response.data?.length || 0,
    };
  },
});
